import React, { useEffect, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { LogOut, Save, RefreshCw, UploadCloud, FileJson, Image as ImageIcon, Settings, Languages } from "lucide-react";
import { TR } from "@/data/translations";
import { SITE as DEFAULT_SITE } from "@/data/content";
import JsonEditor, { setByPath, removeByPath } from "@/components/admin/JsonEditor";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const TOKEN_KEY = "cms_token";

const authHeader = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const DEFAULT_CONTENT = { tr: TR, site: DEFAULT_SITE };

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Zalogowano");
      onLogin(data.user);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nie udało się zalogować.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-100 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-stone-200 p-8"
        data-testid="admin-login-form"
      >
        <h1 className="text-2xl font-serif text-stone-800 mb-1">Panel — PrzyStań w Ciszy</h1>
        <p className="text-sm text-stone-500 mb-6">Zaloguj się, aby edytować treść strony.</p>
        <label className="block text-xs font-medium uppercase tracking-wide text-stone-400 mb-1">E-mail</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="admin-login-email"
          className="w-full mb-4 rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-red-800 focus:outline-none"
        />
        <label className="block text-xs font-medium uppercase tracking-wide text-stone-400 mb-1">Hasło</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          data-testid="admin-login-password"
          className="w-full mb-6 rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-red-800 focus:outline-none"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-login-submit"
          className="w-full rounded-md bg-red-800 py-2.5 text-sm font-medium text-white hover:bg-red-900 disabled:opacity-60"
        >
          {busy ? "Logowanie…" : "Zaloguj się"}
        </button>
      </form>
      <Toaster position="top-center" richColors />
    </div>
  );
}

function ImageUploader() {
  const [file, setFile] = useState(null);
  const [filename, setFilename] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) return toast.error("Wybierz plik.");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (filename.trim()) fd.append("filename", filename.trim());
      const { data } = await axios.post(`${API}/upload`, fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      setResult(data.url);
      toast.success("Zdjęcie wgrane.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nie udało się wgrać pliku.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl">
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 mb-6">
        <strong>Podmiana zdjęcia:</strong> aby zastąpić istniejące zdjęcie na stronie, wpisz w polu
        „Nazwa docelowa" dokładnie taką samą nazwę pliku, jaka jest teraz (np. <code>by1.webp</code>,
        <code> atm-book.webp</code>). Nowe zdjęcie nadpisze stare pod tym samym adresem.
      </div>
      <label className="block text-xs font-medium uppercase tracking-wide text-stone-400 mb-1">Plik (jpg, png, webp, avif)</label>
      <input
        type="file"
        accept="image/*"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        data-testid="admin-upload-file"
        className="w-full mb-4 text-sm"
      />
      <label className="block text-xs font-medium uppercase tracking-wide text-stone-400 mb-1">Nazwa docelowa (opcjonalnie)</label>
      <input
        type="text"
        value={filename}
        onChange={(e) => setFilename(e.target.value)}
        placeholder="np. by1.webp"
        data-testid="admin-upload-filename"
        className="w-full mb-4 rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-red-800 focus:outline-none"
      />
      <button
        onClick={upload}
        disabled={busy}
        data-testid="admin-upload-submit"
        className="inline-flex items-center gap-2 rounded-md bg-red-800 px-4 py-2 text-sm font-medium text-white hover:bg-red-900 disabled:opacity-60"
      >
        <UploadCloud size={16} /> {busy ? "Wgrywanie…" : "Wgraj zdjęcie"}
      </button>
      {result && (
        <div className="mt-5 rounded-md border border-stone-200 bg-white p-3">
          <p className="text-xs text-stone-500 mb-2">Adres zdjęcia: <code>{result}</code></p>
          <img src={result} alt="Podgląd" className="max-h-48 rounded" />
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("pl");
  const [saving, setSaving] = useState(false);

  // Verify existing token
  useEffect(() => {
    document.title = "Panel — PrzyStań w Ciszy";
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return setChecking(false);
    axios
      .get(`${API}/auth/me`, { headers: authHeader() })
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  // Load content once logged in
  useEffect(() => {
    if (!user) return;
    loadContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadContent = async () => {
    try {
      const { data } = await axios.get(`${API}/content`);
      const hasContent = data && data.tr && data.site;
      setContent(hasContent ? { tr: data.tr, site: data.site } : DEFAULT_CONTENT);
    } catch {
      setContent(DEFAULT_CONTENT);
      toast.message("Wczytano treści domyślne (brak połączenia z bazą).");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setContent(null);
  };

  const handleChange = (path, val, isRemove) => {
    setContent((c) => (isRemove ? removeByPath(c, path) : setByPath(c, path, val)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/content`,
        { tr: content.tr, site: content.site },
        { headers: authHeader() }
      );
      toast.success("Zapisano zmiany.");
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sesja wygasła — zaloguj się ponownie.");
        logout();
      } else {
        toast.error(err?.response?.data?.detail || "Nie udało się zapisać.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-stone-400">Ładowanie…</div>;
  }
  if (!user) return <Login onLogin={setUser} />;

  const tabs = [
    { id: "pl", label: "Treści PL", icon: Languages },
    { id: "en", label: "Treści EN", icon: Languages },
    { id: "site", label: "Ustawienia", icon: Settings },
    { id: "images", label: "Zdjęcia", icon: ImageIcon },
  ];

  return (
    <div className="min-h-screen bg-stone-100">
      <Toaster position="top-center" richColors />
      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-stone-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <FileJson size={18} className="text-red-800" />
            <span className="font-serif text-lg text-stone-800">PrzyStań w Ciszy — Panel</span>
          </div>
          <div className="flex items-center gap-2">
            {content && tab !== "images" && (
              <button
                onClick={save}
                disabled={saving}
                data-testid="admin-save"
                className="inline-flex items-center gap-2 rounded-md bg-red-800 px-4 py-2 text-sm font-medium text-white hover:bg-red-900 disabled:opacity-60"
              >
                <Save size={16} /> {saving ? "Zapisywanie…" : "Zapisz zmiany"}
              </button>
            )}
            <button
              onClick={loadContent}
              title="Przeładuj treści"
              data-testid="admin-reload"
              className="rounded-md border border-stone-300 p-2 text-stone-500 hover:text-stone-800"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-md border border-stone-300 px-3 py-2 text-sm text-stone-600 hover:text-red-800"
            >
              <LogOut size={16} /> Wyloguj
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="mx-auto flex max-w-5xl gap-1 px-4">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              data-testid={`admin-tab-${tb.id}`}
              className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm ${
                tab === tb.id
                  ? "border-red-800 text-red-800"
                  : "border-transparent text-stone-500 hover:text-stone-800"
              }`}
            >
              <tb.icon size={15} /> {tb.label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8">
        {!content && tab !== "images" ? (
          <p className="text-stone-400">Ładowanie treści…</p>
        ) : tab === "images" ? (
          <ImageUploader />
        ) : tab === "site" ? (
          <div className="rounded-xl border border-stone-200 bg-white p-5">
            <p className="text-sm text-stone-500 mb-4">
              Dane kontaktowe, linki rezerwacji, adres, mapa i social media.
            </p>
            <JsonEditor data={content.site} section="site" onChange={handleChange} defaultOpen />
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-stone-500">
              Kliknij sekcję, aby ją rozwinąć i edytować teksty ({tab === "pl" ? "polski" : "angielski"}).
              Pamiętaj o zapisaniu zmian.
            </p>
            {Object.keys(content.tr[tab]).map((sec) => (
              <div key={sec} className="rounded-xl border border-stone-200 bg-white p-4">
                <JsonEditor
                  data={content.tr[tab][sec]}
                  section={sec}
                  defaultOpen={false}
                  onChange={(path, val, isRemove) =>
                    handleChange(["tr", tab, ...path], val, isRemove)
                  }
                />
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
